import { useState, useMemo } from 'react';
import { useVantifyContext } from 'integrations-vantify-context-providers';
import {
  addWorkOrderAttachment,
  AddWorkOrderAttachmentParams,
  cancelWorkOrder,
  createWorkOrder,
  deleteWorkOrderAttachment,
  DeleteWorkOrderAttachmentParams,
  FetchWorkOrderDetailsParams,
  FetchWorkOrderFilesParams,
  FetchWorkOrdersParams,
  getWorkOrderDetails,
  getWorkOrderFiles,
  getWorkOrders,
  uploadFile,
  UploadFileParams,
  WorkOrder,
  WorkOrderListResponse,
  acceptWorkOrderApproval,
  AcceptWorkOrderApprovalParams,
  acceptWorkOrderAssignment,
} from 'integrations-vantify-client-services';
import { useVantifyBase } from '../useVantifyBase';
import { hookMessages, VantifyOperation } from '../common';
import { VantifyProxyResponse } from '../user';
import { useTenantInfo } from '../tenant/useTenantInfo';
import {
  VantifyContentType,
  CreateWorkOrderRequestParams,
  VantifyWorkOrderData,
  WorkOrderReporter,
  WorkOrderSiteContact,
  TenantMapping,
} from './types';

export function useWorkOrder() {
  const { auth, user, content } = useVantifyContext();
  const { loading, error, execute, setError } = useVantifyBase();
  const [workOrderDetail, setWorkOrderDetail] = useState<WorkOrder | null>(
    null
  );
  const [workOrders, setWorkOrders] = useState<WorkOrderListResponse | null>(
    null
  );

  const tenantInfo = useTenantInfo(auth?.channelId);
  const tenantId = tenantInfo?.id;

  function isVantifyContent(obj: unknown): obj is VantifyContentType {
    if (!obj || typeof obj !== 'object') return false;

    return 'settings' in obj || 'integration' in obj;
  }

  const vantifyContent: VantifyContentType | null = isVantifyContent(content)
    ? content
    : null;

  const siteId = useMemo(() => {
    const settings = vantifyContent?.integration?.settings;
    const siteIdValue = settings?.siteId || (typeof settings?.site === 'object' ? settings.site?.value : null);

    return siteIdValue ? Number(siteIdValue) : null;
  }, [vantifyContent]);

  const userId = useMemo(() => {
    const userIdValue = vantifyContent?.integration?.settings?.userId;

    return userIdValue ? String(userIdValue) : null;
  }, [vantifyContent]);

  const reporter = useMemo<WorkOrderReporter>(() => {
    return {
      name: user?.name || '',
      email: user?.email || '',
    };
  }, [user]);

  const siteContact = useMemo<WorkOrderSiteContact>(() => {
    const integration = vantifyContent?.integration;
    const settings = integration?.settings;
    const tenantMappings = Array.isArray(settings?.tenantMappings)
      ? settings.tenantMappings
      : [];
    const matchingMapping = tenantMappings.find((mapping: TenantMapping) => {
      if (!mapping?.vtsTenantId) {
        return false;
      }

      return mapping.vtsTenantId === tenantId;
    });

    return {
      name: matchingMapping?.vantifyTenantName || '',
      email: matchingMapping?.vantifyTenantEmail || '',
    };
  }, [tenantId, vantifyContent]);

   const extractEmailDomain = (email: string | null | undefined): string | null => {
    if (!email) {
      return null;
    }

    const parts = email.split('@');

    return parts.length === 2 ? parts[1].toLowerCase() : null;
  };

  const isEmailDomainApproved = useMemo(() => {
    const userEmail = user?.email;
    const userDomain = extractEmailDomain(userEmail);
    const settings = vantifyContent?.integration?.settings;
    const approvedDomainsArray = settings?.approvedDomains;

    if (!Array.isArray(approvedDomainsArray)) {
      return false;
    }

    const approvedDomains = approvedDomainsArray
      .filter((domain): domain is string => typeof domain === 'string')
      .map(domain => domain.toLowerCase());

    if (!userDomain || approvedDomains.length === 0) {
      return false;
    }

    return approvedDomains.includes(userDomain);
  }, [user?.email, vantifyContent]);

  const shouldAutoApprove = useMemo(() => {
    const settings = vantifyContent?.integration?.settings;
    const autoApproveEnabled = settings?.autoApproveWorkOrders === true;

    return autoApproveEnabled && isEmailDomainApproved;
  }, [vantifyContent, isEmailDomainApproved]);

  const fetchWorkOrderDetails = async (
    fetchParams: FetchWorkOrderDetailsParams
  ) => {
    if (!auth) {
      setError(e => ({
        ...e,
        [VantifyOperation.FETCH_WORK_ORDER_DETAILS]:
          hookMessages.error_FETCH_WORK_ORDER_DETAILS,
      }));

      return null;
    }

    const response = await execute(
      VantifyOperation.FETCH_WORK_ORDER_DETAILS,
      () => getWorkOrderDetails(auth, fetchParams),
      hookMessages.error_FETCH_WORK_ORDER_DETAILS
    );

    if (response?.data) {
      setWorkOrderDetail(response.data);

      return response.data;
    }

    return null;
  };

  const fetchWorkOrders = async (fetchParams: FetchWorkOrdersParams) => {
    if (!auth) {
      setError(e => ({
        ...e,
        [VantifyOperation.FETCH_WORK_ORDERS]:
          hookMessages.error_FETCH_WORK_ORDERS,
      }));

      return null;
    }

    const response = await execute(
      VantifyOperation.FETCH_WORK_ORDERS,
      () => getWorkOrders(auth, fetchParams),
      hookMessages.error_FETCH_WORK_ORDERS
    );

    if (response?.data) {
      setWorkOrders(response.data);

      return response.data;
    }

    return null;
  };

  const fetchWorkOrderFiles = async (
    fetchParams: FetchWorkOrderFilesParams
  ): Promise<VantifyProxyResponse | null> => {
    if (!auth) {
      setError(e => ({
        ...e,
        [VantifyOperation.FETCH_WORK_ORDER_FILES]:
          hookMessages.error_FETCH_WORK_ORDER_FILES,
      }));

      return null;
    }

    return await execute(
      VantifyOperation.FETCH_WORK_ORDER_FILES,
      () => getWorkOrderFiles(auth, fetchParams),
      hookMessages.error_FETCH_WORK_ORDER_FILES
    );
  };

  const createWorkOrderRequest = async (
    params: CreateWorkOrderRequestParams
  ): Promise<VantifyProxyResponse | null> => {
    if (!auth) {
      setError(e => ({
        ...e,
        [VantifyOperation.CREATE_WORK_ORDER]:
          hookMessages.error_CREATE_WORK_ORDER,
      }));

      return null;
    }

    const { approverUserId, serviceProviderId, ...createParams } = params;

    const response = await execute(
      VantifyOperation.CREATE_WORK_ORDER,
      () => createWorkOrder(auth, createParams),
      hookMessages.error_CREATE_WORK_ORDER
    );

    // Auto-approve work order if conditions are met
    if (response?.data?.id && siteId && shouldAutoApprove) {
      const jobGroupId = params.jobGroupId;

      if (jobGroupId && approverUserId && serviceProviderId) {
        const approvalResponse = await execute(
          VantifyOperation.ACCEPT_WORK_ORDER_APPROVAL,
          () =>
            acceptWorkOrderApproval(auth, {
              siteId,
              jobId: response.data.id,
              userId: approverUserId,
              serviceProviderId,
              jobGroupId,
              summary: params.summary,
              description: params.description,
            }),
          hookMessages.error_ACCEPT_WORK_ORDER_APPROVAL
        );

        if (approvalResponse?.status === 204) {
          await execute(
            VantifyOperation.ACCEPT_WORK_ORDER_ASSIGNMENT,
            () =>
              acceptWorkOrderAssignment(auth, {
                siteId,
                jobId: response.data.id,
              }),
            hookMessages.error_ACCEPT_WORK_ORDER_ASSIGNMENT
          );
        }
      }
    }

    return response;
  };

  const cancelWorkOrderRequest = async (
    siteId: number,
    jobId: number,
    note: string
  ): Promise<VantifyProxyResponse | null> => {
    if (!auth) {
      setError(e => ({
        ...e,
        [VantifyOperation.CANCEL_WORK_ORDER]:
          hookMessages.error_CANCEL_WORK_ORDER,
      }));

      return null;
    }

    return await execute(
      VantifyOperation.CANCEL_WORK_ORDER,
      () => cancelWorkOrder(auth, siteId, jobId, note),
      hookMessages.error_CANCEL_WORK_ORDER
    );
  };

  const workOrderData: VantifyWorkOrderData = useMemo(
    () => ({
      siteId,
      userId,
      reporter,
      siteContact,
    }),
    [siteId, userId, reporter, siteContact]
  );

  const addWorkOrderAttachmentRequest = async (
    params: AddWorkOrderAttachmentParams
  ): Promise<VantifyProxyResponse | null> => {
    if (!auth) {
      setError(e => ({
        ...e,
        [VantifyOperation.ADD_WORK_ORDER_ATTACHMENT]:
          hookMessages.error_ADD_WORK_ORDER_ATTACHMENT,
      }));

      return null;
    }

    return await execute(
      VantifyOperation.ADD_WORK_ORDER_ATTACHMENT,
      () => addWorkOrderAttachment(auth, params),
      hookMessages.error_ADD_WORK_ORDER_ATTACHMENT
    );
  };

  const deleteWorkOrderAttachmentRequest = async (
    params: DeleteWorkOrderAttachmentParams
  ): Promise<VantifyProxyResponse | null> => {
    if (!auth) {
      setError(e => ({
        ...e,
        [VantifyOperation.DELETE_WORK_ORDER_ATTACHMENT]:
          hookMessages.error_DELETE_WORK_ORDER_ATTACHMENT,
      }));

      return null;
    }

    return await execute(
      VantifyOperation.DELETE_WORK_ORDER_ATTACHMENT,
      () => deleteWorkOrderAttachment(auth, params),
      hookMessages.error_DELETE_WORK_ORDER_ATTACHMENT
    );
  };

    const uploadFileRequest = async (
    params: UploadFileParams
  ): Promise<VantifyProxyResponse | null> => {
    if (!auth) {
      setError(e => ({
        ...e,
        [VantifyOperation.UPLOAD_FILE]:
          hookMessages.error_UPLOAD_FILE,
      }));

      return null;
    }

    return await execute(
      VantifyOperation.UPLOAD_FILE,
      () => uploadFile(auth, params),
      hookMessages.error_UPLOAD_FILE
    );
  };

  const acceptWorkOrderApprovalRequest = async (
    params: AcceptWorkOrderApprovalParams
  ): Promise<VantifyProxyResponse | null> => {
    if (!auth) {
      setError(e => ({
        ...e,
        [VantifyOperation.ACCEPT_WORK_ORDER_APPROVAL]:
          hookMessages.error_ACCEPT_WORK_ORDER_APPROVAL,
      }));

      return null;
    }

    return await execute(
      VantifyOperation.ACCEPT_WORK_ORDER_APPROVAL,
      () => acceptWorkOrderApproval(auth, params),
      hookMessages.error_ACCEPT_WORK_ORDER_APPROVAL
    );
  };

  return {
    workOrderDetail,
    workOrders,
    loading,
    error,
    fetchWorkOrderDetails,
    fetchWorkOrders,
    fetchWorkOrderFiles,
    createWorkOrder: createWorkOrderRequest,
    cancelWorkOrder: cancelWorkOrderRequest,
    ...workOrderData,
    addWorkOrderAttachment: addWorkOrderAttachmentRequest,
    deleteWorkOrderAttachment: deleteWorkOrderAttachmentRequest,
    uploadFile: uploadFileRequest,
    acceptWorkOrderApproval: acceptWorkOrderApprovalRequest,
    shouldAutoApprove,
  };
}